import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { offerApi } from '@/api';
import {
  Alert,
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  CardActions,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  ListCard,
  LoadingState,
  Modal,
  PageHeader,
  Pagination,
  Textarea,
  useToast,
} from '@/components/ui';
import {
  OFFER_TONES,
  STUDENT_OFFER_LABELS,
  respondByLine,
  stipendLine,
} from '@/features/offers/status';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate, humanizeWorkMode } from '@/utils/format';

const PAGE_SIZE = 20;

/** The icon tile takes the badge's tone, so an accepted offer reads green. */
const TILE_TONES = { SENT: 'brand', ACCEPTED: 'success', DECLINED: 'danger' };

/** The stripe: the offer's status, or "waiting on you" while it can be answered. */
function accentFor(offer) {
  if (offer.can_respond) return 'warning';
  return OFFER_TONES[offer.status] ?? 'neutral';
}

export function StudentOffersPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [answering, setAnswering] = useState(null);
  const [message, setMessage] = useState('');

  /** Opens the answer dialog with an empty message: text typed for a
   * decline and then backed out of must not reappear on an accept. */
  function openAnswer(next) {
    setMessage('');
    setAnswering(next);
  }

  const params = { page, page_size: PAGE_SIZE };

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['student-offers', params],
    queryFn: () => offerApi.listOffers(params),
  });

  const answer = useMutation({
    mutationFn: ({ id, accept, message: note }) =>
      accept ? offerApi.acceptOffer(id, note) : offerApi.declineOffer(id, note),
    onSuccess: (offer) => {
      toast.success(
        offer.status === 'ACCEPTED'
          ? 'Accepted. Congratulations!'
          : 'Declined. The company has been told.',
      );
      void queryClient.invalidateQueries({ queryKey: ['student-offers'] });
      void queryClient.invalidateQueries({ queryKey: ['student-dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['student-applications'] });
      setAnswering(null);
      setMessage('');
    },
    onError: (answerError) => toast.error(errorMessage(answerError, 'Could not send your answer')),
  });

  // Drafts never reach this page, so every offer here has a letter.
  const letter = useMutation({
    mutationFn: (id) => offerApi.downloadOfferLetter(id),
    onError: (letterError) =>
      toast.error(errorMessage(letterError, 'Could not download the offer letter')),
  });

  const rows = data?.data ?? [];
  const meta = data?.meta;
  const waiting = rows.filter((row) => row.can_respond).length;

  return (
    // On a phone or tablet held upright the list card runs to the bottom of the
    // screen.
    <div className="flex flex-col portrait:flex-1">
      <PageHeader title="Offers" description="Placements you have been offered." />

      {waiting > 0 && (
        <Alert tone="warning" className="mb-6" title="Waiting for your answer">
          {waiting === 1
            ? 'One offer is still open. Accepting it withdraws nothing else automatically — check your other applications.'
            : `${waiting} offers are still open.`}
        </Alert>
      )}

      <ListCard
        fill
        className="flex-1"
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading your offers…" />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Icon name="document" size={22} />}
              title="No offers yet"
              description="Offers appear here once a company decides to make one."
              action={
                <LinkButton to={paths.student.applications} variant="secondary" size="sm">
                  See your applications
                </LinkButton>
              }
            />
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((offer) => {
            const deadline = respondByLine(offer);
            return (
              <BoxRow
                key={offer.id}
                accent={accentFor(offer)}
                leading={<BoxAvatar icon="document" tone={TILE_TONES[offer.status] ?? 'neutral'} />}
                title={offer.title}
                badges={
                  <Badge tone={OFFER_TONES[offer.status] ?? 'neutral'} dot>
                    {STUDENT_OFFER_LABELS[offer.status] ?? offer.status}
                  </Badge>
                }
                subtitle={
                  <span className="text-ink-muted">
                    For <span className="text-ink font-semibold">{offer.internship_title}</span> at{' '}
                    <Link
                      to={paths.companyPublic(offer.company_slug)}
                      className="text-ink hover:text-brand-700 font-semibold hover:underline"
                    >
                      {offer.company_name}
                    </Link>
                  </span>
                }
                facts={[
                  { icon: 'billing', text: stipendLine(offer), tone: 'brand' },
                  {
                    icon: 'calendar',
                    text: offer.ends_on
                      ? `${formatDate(offer.starts_on)} – ${formatDate(offer.ends_on)}`
                      : `Starts ${formatDate(offer.starts_on)}`,
                  },
                  offer.weekly_hours && {
                    icon: 'clockOutline',
                    text: `${offer.weekly_hours}h a week`,
                  },
                  { icon: 'company', text: humanizeWorkMode(offer.work_mode) },
                  offer.location && { icon: 'home', text: offer.location },
                  deadline && {
                    icon: 'alert',
                    text: `${deadline} — by ${formatDate(offer.respond_by)}`,
                    tone: 'warning',
                  },
                ]}
                meta={
                  <span className="text-ink-muted text-xs">
                    Sent {formatDate(offer.sent_at ?? offer.created_at)}
                  </span>
                }
                actions={
                  <CardActions>
                    <Button
                      variant={offer.can_respond ? 'secondary' : 'tintBrand'}
                      size="sm"
                      title="Offer letter (PDF)"
                      isLoading={letter.isPending && letter.variables === offer.id}
                      onClick={() => letter.mutate(offer.id)}
                    >
                      <Icon name="download" size={14} />
                      Offer letter
                    </Button>
                    {offer.can_respond && (
                      <>
                        <Button
                          variant="tintDanger"
                          size="sm"
                          onClick={() => openAnswer({ offer, accept: false })}
                        >
                          <Icon name="close" size={14} />
                          Decline
                        </Button>
                        <Button
                          variant="tintPositive"
                          size="sm"
                          onClick={() => openAnswer({ offer, accept: true })}
                        >
                          <Icon name="check" size={14} />
                          Accept
                        </Button>
                      </>
                    )}
                  </CardActions>
                }
              >
                {/* Terms are what the student is agreeing to, so unlike a
                    description they are never clamped. */}
                {offer.message && (
                  <p className="text-ink-muted mt-2.5 text-sm leading-6 whitespace-pre-line">
                    {offer.message}
                  </p>
                )}
                {offer.terms && (
                  <p className="bg-surface-muted/60 rounded-input text-ink-muted mt-2.5 px-3 py-2 text-xs leading-5 whitespace-pre-line">
                    {offer.terms}
                  </p>
                )}
                {offer.withdrawal_reason && (
                  <p className="text-ink-muted mt-2 text-xs">
                    The company withdrew this offer: {offer.withdrawal_reason}
                  </p>
                )}
              </BoxRow>
            );
          })}
        </BoxList>
      </ListCard>

      <Modal
        open={Boolean(answering)}
        title={
          answering?.accept
            ? `Accept the offer from ${answering?.offer.company_name}?`
            : `Decline the offer from ${answering?.offer.company_name}?`
        }
        description={
          answering?.accept
            ? 'The company then marks you as joined on your first day. Accepting cannot be undone here.'
            : 'Declining also takes you out of that company’s pipeline.'
        }
        onClose={() => setAnswering(null)}
      >
        <div className="space-y-4">
          <Textarea
            label="Message"
            rows={3}
            hint="Optional. The company reads this."
            placeholder={
              answering?.accept
                ? 'Delighted — see you in September.'
                : 'Thank you, but I have accepted somewhere else.'
            }
            value={message}
            onChange={(event) => setMessage(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAnswering(null)}>
              Back
            </Button>
            <Button
              variant={answering?.accept ? 'primary' : 'danger'}
              isLoading={answer.isPending}
              onClick={() =>
                answer.mutate({
                  id: answering.offer.id,
                  accept: answering.accept,
                  message: message.trim() || null,
                })
              }
            >
              {answering?.accept ? 'Accept offer' : 'Decline offer'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
